import { useState, useEffect } from 'react';

/**
 * Custom hook for screen size detection with a debounced resize handler.
 * Each caller registers its own listener; the listener and any pending
 * debounce timeout are cleaned up on unmount.
 */

export type ScreenSize = 'mobile' | 'tablet' | 'desktop';

export interface ScreenSizeConfig {
  mobile: number;   // < 640px
  tablet: number;   // 640px - 1023px  
  desktop: number;  // >= 1024px
}

const DEFAULT_BREAKPOINTS: ScreenSizeConfig = {
  mobile: 640,
  tablet: 1024,
  desktop: 1024
};

export const useScreenSize = (
  breakpoints: ScreenSizeConfig = DEFAULT_BREAKPOINTS,
  debounceMs: number = 150
) => {
  // Initialize with fallback values for SSR
  const [screenWidth, setScreenWidth] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth;
    }
    return DEFAULT_BREAKPOINTS.desktop; // Fallback for SSR
  });

  const [screenSize, setScreenSize] = useState<ScreenSize>(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : DEFAULT_BREAKPOINTS.desktop;
    if (width < breakpoints.mobile) return 'mobile';
    if (width < breakpoints.tablet) return 'tablet';
    return 'desktop';
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);

      let newSize: ScreenSize;
      if (width < breakpoints.mobile) {
        newSize = 'mobile';
      } else if (width < breakpoints.tablet) {
        newSize = 'tablet';
      } else {
        newSize = 'desktop';
      }

      setScreenSize(newSize);
    };

    // Debounced resize handler
    const debouncedUpdate = () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        timeout = null;
        updateScreenSize();
      }, debounceMs);
    };

    // Initial update
    updateScreenSize();

    // Add event listener
    window.addEventListener('resize', debouncedUpdate);

    // Cleanup — remove listener and cancel any pending debounce so we
    // never setState after unmount
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [breakpoints.mobile, breakpoints.tablet, debounceMs]);

  return {
    screenWidth,
    screenSize,
    isMobile: screenSize === 'mobile',
    isTablet: screenSize === 'tablet', 
    isDesktop: screenSize === 'desktop',
    breakpoints
  };
};

export default useScreenSize;